import { useState } from "react";
import Button from "../../../shared/components/Button";
import { TextField } from "../../../shared/components/form";
import { FloatingWindow } from "../../../shared/components/FloatingWindow";
import { discoverCameras, type DiscoveryResult } from "../api/camera-scan-api";
import "./RTSPScannerModal.scss";

type RTSPScannerModalProps = {
  onClose: () => void;
  onSelect: (source: string) => void;
};

export function RTSPScannerModal({ onClose, onSelect }: RTSPScannerModalProps) {
  const [target, setTarget] = useState("10.0.0.0/24");
  const [results, setResults] = useState<DiscoveryResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");

  const scan = async () => {
    setScanning(true);
    setError("");
    try {
      setResults(await discoverCameras(target));
    } catch (scanError) {
      setError(
        scanError instanceof Error ? scanError.message : "Camera scan failed",
      );
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="rtsp-scanner-modal">
      <FloatingWindow
        className="rtsp-scanner-panel"
        onClose={onClose}
        subtitle="Find RTSP and ONVIF services on one host or IPv4 subnet."
        title="Scan Network"
      >
        <div className="rtsp-scanner-form">
          <TextField
            aria-label="IP address or CIDR to scan"
            label="Network target"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          />
          <Button disabled={scanning} onClick={() => void scan()}>
            {scanning ? "Scanning…" : "Scan"}
          </Button>
        </div>
        <p className="rtsp-scanner-help">
          Scan is limited to 256 IPv4 addresses. Credentials are not used.
        </p>

        {error && <p className="rtsp-scanner-error">{error}</p>}
        <div className="rtsp-scanner-results">
          {results.length === 0 ? (
            <p className="rtsp-scanner-empty">No discoveries yet.</p>
          ) : (
            results.map((result) => (
              <article className="rtsp-discovery" key={result.host}>
                <div>
                  <strong>{result.host}</strong>
                  <span>
                    {result.rtsp_urls?.length
                      ? `${result.rtsp_urls.length} RTSP source${result.rtsp_urls.length === 1 ? "" : "s"} found`
                      : "No RTSP URL found"}
                  </span>
                </div>
                {result.rtsp_urls?.map((source) => (
                  <Button
                    className="rtsp-source"
                    key={source}
                    onClick={() => onSelect(source)}
                    variant="secondary"
                  >
                    Use {source}
                  </Button>
                ))}
              </article>
            ))
          )}
        </div>
      </FloatingWindow>
    </div>
  );
}
